import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Facebook, Instagram, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AutomationsNetwork = 'whatsapp' | 'instagram' | 'facebook';

export type AutomationsNetworkTab<TId extends string = AutomationsNetwork> = {
  id: TId;
  label: string;
  count?: number;
  Icon: ComponentType<{ className?: string }>;
  show: boolean;
};

export const AUTOMATIONS_NETWORK_META: Record<
  AutomationsNetwork,
  { label: string; Icon: ComponentType<{ className?: string }> }
> = {
  whatsapp: { label: 'WhatsApp', Icon: MessageCircle },
  instagram: { label: 'Instagram', Icon: Instagram },
  facebook: { label: 'Facebook', Icon: Facebook },
};

export function parseAutomationsNetwork(raw: string | null | undefined): AutomationsNetwork {
  if (raw === 'instagram' || raw === 'facebook' || raw === 'whatsapp') return raw;
  return 'whatsapp';
}

export const AUTOMATIONS_FILTER_PARAMS = {
  whatsapp: {
    search: 'search',
    status: 'status',
    trigger: 'trigger',
    page: 'page',
    limit: 'limit',
  },
  instagram: {
    search: 'ig_q',
    status: 'ig_status',
    trigger: 'ig_trigger',
    page: 'ig_page',
    limit: 'ig_limit',
  },
  facebook: {
    search: 'fb_q',
    status: 'fb_status',
    trigger: 'fb_trigger',
  },
} as const;

export const AUTOMATIONS_LEGACY_SHARED_SEARCH_PARAM = 'q';

type AutomationsWorkspaceShellProps<TId extends string = AutomationsNetwork> = {
  network: TId;
  tabs: AutomationsNetworkTab<TId>[];
  onNetworkChange: (network: TId) => void;
  filterBar?: ReactNode;
  header?: ReactNode;
  hideTabs?: boolean;
  rightRail?: ReactNode;
  children: ReactNode;
};

export function AutomationsWorkspaceShell<TId extends string = AutomationsNetwork>({
  network,
  tabs,
  onNetworkChange,
  filterBar,
  header,
  hideTabs = false,
  rightRail,
  children,
}: AutomationsWorkspaceShellProps<TId>) {
  const visibleTabs = hideTabs ? [] : tabs.filter((t) => t.show);

  const networkTabsScrollRef = useRef<HTMLDivElement>(null);
  const [networkTabsOverflow, setNetworkTabsOverflow] = useState({ left: false, right: false });
  const updateNetworkTabsOverflow = useCallback(() => {
    const el = networkTabsScrollRef.current;
    if (!el) {
      setNetworkTabsOverflow({ left: false, right: false });
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setNetworkTabsOverflow({
      left: scrollLeft > 2,
      right: scrollLeft + clientWidth < scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    const el = networkTabsScrollRef.current;
    if (!el) return;
    updateNetworkTabsOverflow();
    el.addEventListener('scroll', updateNetworkTabsOverflow, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateNetworkTabsOverflow) : null;
    ro?.observe(el);
    window.addEventListener('resize', updateNetworkTabsOverflow);
    return () => {
      el.removeEventListener('scroll', updateNetworkTabsOverflow);
      ro?.disconnect();
      window.removeEventListener('resize', updateNetworkTabsOverflow);
    };
  }, [updateNetworkTabsOverflow, visibleTabs.length]);

  useEffect(() => {
    const root = networkTabsScrollRef.current;
    if (!root) return;
    const active = root.querySelector<HTMLElement>('[data-network-tab-active="true"]');
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    const t = window.setTimeout(updateNetworkTabsOverflow, 350);
    return () => window.clearTimeout(t);
  }, [network, updateNetworkTabsOverflow, visibleTabs.length]);

  return (
    <div className="flex h-full min-h-full bg-white">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
      {visibleTabs.length > 0 ? (
        <div className="shrink-0 border-b border-[#EAECF0] bg-white px-[15px] pt-3">
          <div className="relative">
            {networkTabsOverflow.left ? (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 z-10 w-7 bg-gradient-to-r from-white via-white/90 to-transparent sm:hidden"
              />
            ) : null}
            {networkTabsOverflow.right ? (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 z-10 w-7 bg-gradient-to-l from-white via-white/90 to-transparent sm:hidden"
              />
            ) : null}
            <div
              ref={networkTabsScrollRef}
              data-testid="automations-network-tabs"
              className="flex items-end gap-1 overflow-x-auto overflow-y-hidden overscroll-x-contain scrollbar-hide touch-pan-x [-webkit-overflow-scrolling:touch]"
            >
              {visibleTabs.map((t) => {
                const active = t.id === network;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-network-tab-active={active ? 'true' : undefined}
                    onClick={() => onNetworkChange(t.id)}
                    className={cn(
                      'relative inline-flex items-center gap-2 whitespace-nowrap px-3 pb-3 pt-2 text-[13.5px] font-semibold transition',
                      active ? 'text-[#101828]' : 'text-[#6B7280] hover:text-[#101828]',
                    )}
                  >
                    <t.Icon
                      className={cn('h-4 w-4 shrink-0', active ? 'text-[#475467]' : 'text-[#98A2B3]')}
                    />
                    {t.label}
                    {typeof t.count === 'number' ? (
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-[1px] text-[11px] font-semibold',
                          active ? 'bg-brand-soft text-brand-cta' : 'bg-[#F2F4F7] text-[#6B7280]',
                        )}
                      >
                        {t.count}
                      </span>
                    ) : null}
                    {active ? (
                      <span
                        aria-hidden
                        className="absolute bottom-[-1px] left-3 right-3 h-[2px] rounded-t bg-brand"
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

        {header ? <div className="shrink-0">{header}</div> : null}

        {filterBar ? <div className="shrink-0">{filterBar}</div> : null}

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>
      </div>

      {rightRail}
    </div>
  );
}
